/**
 * 业务页面中 配合宽表组件 PerformanceMetricsTable使用的逻辑
 */
import React, { useMemo, useState } from "react";
import { useIntl, getLocale, useSelector } from "umi";
import moment from "moment";
import MetricAndUpDown from "@/pages/dashboard/components/MetricAndUpDown";
import { Bubble } from "tea-component";
import {
  formatAccumulation, formatFilterValues, formatPercentNum, formatNum, commonValueFormatter,
  generateCopyData, getUpAndDownStatus, getWeekDay, getDynamicLocalCnEnText, formatSmallValue
} from '@/pages/dashboard/utils/utils';
import { PagingQuery, SortBy, TableParamsType, metricsInfoType } from "./types";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";
import { resolveAndSelectName } from "@/pages/dashboard/utils/metricUtils";
import { TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from "@/pages/home/_config/config";
// import { thousandthFormat } from "@/pages/home/utils/common";
import SvgIcon from "@/pages/home/components/SvgIcon";
import { useWeekDayArr } from "@/pages/dashboard/utils/customHook";
import { getVal } from "../../_config/utils";
import { getCountryFlag } from "@/pages/home/_config/countries";
import { ACCUMULATED_DEFAULT_VALUE } from "@/pages/dashboard/components/Accumulate/constant";
import { IExplain } from "@/pages/dashboard/types/dashboard";

export function useTableState() {
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  // 分页
  const [paging, setPaging] = useState<PagingQuery>({ pageSize: 10, pageIndex: 1 });
  // 排序
  const [sort, setSort] = useState<SortBy[]>([]);
  // 总条数
  const [total, setTotal] = useState(0);

  // 表格数据
  const [tableData, setTableData] = useState([]);

  // 聚合数据
  const [statisticsData, setStatisticsData] = useState([]);
  return {
    loading, setLoading,
    isError, setIsError,
    paging, setPaging,
    sort, setSort,
    total, setTotal,
    tableData, setTableData,
    statisticsData, setStatisticsData,
  };
}

// 表格上传参数
export function getTableRequestParams({ gameCode, filterValue: filterVal, accumulated, sort, desc, paging, selectType, dataKey, ...resetParams }: TableParamsType) {
  const { by, order } = sort?.[0] || { by: 'date', order: dataKey === 'date_comparison' ? 'desc' : 'asc' };
  const { timeType, diff, rangeTime, comparisonDate, metrics, selector, ...filters } = filterVal || {};
  const params = {
    game_code: gameCode,
    filters: formatFilterValues(filters),
    accumulation: formatAccumulation(accumulated),
    page: paging.pageIndex,
    page_size: paging.pageSize,
    sorted_by: by?.replace(selectType, ''),
    desc: order === 'desc',
    is_switch_rows: false,
    ...resetParams
  };
  // 事件对比不勾选分日期
  if (params.accumulation.not_by_date && dataKey === 'event_comparison') {
    params.is_switch_rows = true;
    params.sorted_by = sort.length === 0 ? '' : params.sorted_by;
  }
  // 特殊逻辑处理: 夜莺游戏日流失指标new_churn_users包含churn_level筛选器,跟产品确认先固定传255
  if (metrics?.includes('new_churn_users')) {
    params.filters = {
      ...formatFilterValues(filters),
      churn_level_list: ['255'],
    };
  }
  return params;
}

export function useTableColumns(filterVal: any, isAccumulated = false, dataKey: string, dataFrame: string, metric_key: string, isHaveContrast: boolean, hasDate: boolean): void {
  const { formatMessage } = useIntl();
  const weekDayArr = useWeekDayArr();
  const isEn = getLocale() === 'en-US';
  const storeDataFrame = useSelector((state: any) => state?.dashboard?.currentGame?.data_frame || 'mobile');
  const isCasual = (dataFrame || storeDataFrame) === 'casual';
  const { timeType, comparisonDate, statisticDate_timeType } = filterVal || {};
  // 日期列, 周六日的颜色高亮,
  let DATE_COLUMN = [];
  if (dataKey === "event_comparison") {
    // 勾选分日期
    if (hasDate) {
      DATE_COLUMN = comparisonDate?.map((item: any, index: number) => {
        const fullEventName = item?.name || '';
        const width = fullEventName.length > 12 ? (fullEventName.length - 10) * 8 : 0;
        if (index === 0) {
          return {
            index: 0,
            key: 'date',
            header: <span title={fullEventName}>{fullEventName}</span>,
            width: isAccumulated ? 220 + width : 170 + width,
            fixed: isCasual ? undefined : "left",
            align: "left",
            render(record: any) {
              return <DateCell record={record} formatMessage={formatMessage} weekDayArr={weekDayArr} timeType={timeType} />;
            }
          };
        }
        const key = 'contrast_date' + `${index > 1 ? `_${  index}` : ''}`;
        return {
          index: 0,
          key,
          header: <span title={fullEventName}>{fullEventName}</span>,
          width: isAccumulated ? 220 + width : 170 + width,
          fixed: isCasual ? undefined : "left",
          align: "left",
          render(record: any) {
            const date = record?.[key];
            const weekDay = getWeekDay(date, weekDayArr)?.slice(1, -1);
            const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
            return isAccumulated ?
              (
                timeType === "Month" && date && date.includes('~') ?
                  date.split('~').map((v: any) => moment(v.trim()).format("YYYY-MM")).join('~') :
                  (date || "-")
              ) : (
                (
                  (!date ?
                    "-" :
                    (timeType === "Month" ?
                      moment(date).format("YYYY-MM") :
                      (
                        <>
                          <span className='table-text'>{moment(date).format("YYYY-MM-DD")}</span>
                          <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>
                        </>
                      )
                    )
                  )
                )
              );
          }
        };

      });
    } else {
      // 不勾选则固定前2列
      DATE_COLUMN = [{
        key: 'event_name',
        name: formatMessage({ id: 'event_comparison_table_first_column' })
      }, {
        key: 'date',
        name: formatMessage({ id: 'event_comparison_table_second_column' })
      }].map((item: any, index: number) => {
        return {
          index,
          key: item.key,
          header: item.name,
          width: isAccumulated ? 220 : 170,
          fixed: "left",
          align: "left",
        };
      });
    }

  } else if (dataKey === "date_comparison") {
    DATE_COLUMN.push(
      {
        key: 'date',
        header: formatMessage({ id: "common_date" }),
        width: isAccumulated ? 220 : 170,
        fixed: "left",
        align: "left",
        render(record: any) {
          return <DateCell record={record} formatMessage={formatMessage} weekDayArr={weekDayArr} timeType={statisticDate_timeType} />;
        }
      },
    );
    if (isHaveContrast) {
      DATE_COLUMN.push(
        {
          key: 'contrast_date',
          header: `${formatMessage({ id: "common_contrast" })  } ${  formatMessage({ id: "common_date" })}`,
          width: isAccumulated ? 220 : 170,
          fixed: "left",
          align: "left",
          render(record: any) {
            const weekDay = getWeekDay(record.contrast_date, weekDayArr)?.slice(1, -1);
            const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
            return isAccumulated ?
              (
                statisticDate_timeType === "Month" && record.contrast_date && record.contrast_date.includes('~') ?
                  record.contrast_date.split('~').map(date => moment(date.trim()).format("YYYY-MM")).join('~') :
                  (record.contrast_date || "")
              ) : (
                (
                  (!record.contrast_date ?
                    "" :
                    (statisticDate_timeType === "Month" ?
                      moment(record.contrast_date).format("YYYY-MM") :
                      (
                        <>
                          <span className='table-text'>{moment(record.contrast_date).format("YYYY-MM-DD")}</span>
                          <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>
                        </>
                      )
                    )
                  )
                )
              );
          }
        }
      );
    }
  }

  // 日期列, 周六日的颜色高亮 & Sum行增加提示
  // eslint-disable-next-line react/prop-types
  function DateCell({ record, formatMessage, weekDayArr, timeType }: any) {
    const [isIconHover, setIsIconHover] = useState(false);
    const weekDay = getWeekDay(record.date, weekDayArr)?.slice(1, -1);
    const locale = getLocale();

    const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
    const showSumTip = record.date === 'Sum' || record.date === '汇总值';

    const renderedDate = !record.date ? '-' : (!!record.type || isAccumulated) ?
      (timeType === "Month" && record.date.includes('~') ?
        record.date.split('~').map(date => moment(date.trim()).format("YYYY-MM")).join('~') :
        record.date
      ) : (
        (timeType === "Month" ?
          moment(record.date).format("YYYY-MM") :
          (
            <>
              <span className='table-text'>{moment(record.date).format("YYYY-MM-DD")}</span>
              <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>
            </>
          )
        )
      );

    return (
      <div style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
        {renderedDate}
        <span style={{ display: 'inline-block', width: '0.4em' }}></span>
        {showSumTip && (
          <div className='port-item'>
            <div onMouseOver={() => { setIsIconHover(true) }} onMouseOut={() => { setIsIconHover(false) }} >
              <Bubble
                dark
                placement="top-start"
                arrowPointAtCenter
                style={{ maxWidth: locale === 'en-US' ? '235px' : '253px' }}
                content={formatMessage({ id: 'overview_sum_tip' })}
              >
                <div><SvgIcon className='port-icon' type={isIconHover ? 'alert-circle-action' : 'alert-circle-m'} /></div>
              </Bubble>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Accumulate 列
  const getAccumulateColumns = (accumulates = [], gameCode: string, selector: any) => {
    if (accumulates[0] === ACCUMULATED_DEFAULT_VALUE) return [];
    return accumulates?.filter(v => v !== 'date')?.map((value) => {
      const width = (value === 'region' || value === 'country') ? 260 : 200; // 设置不同的宽度
      return {
        key: value,
        isDimColumn: true,
        header: formatMessage({ id: `common_${value}` }),
        width,
        align: 'left',
        render(record: any) {
          let ImgDom: JSX.Element;
          try {
            ImgDom = <img style={{ width: '24px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }} src={getCountryFlag(record.country === 'Total' ? 'Global' : record.country)} alt="flag" />;
          } catch (error) {
            ImgDom = <img style={{ width: '24px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }} src={getCountryFlag('undefined')} alt="flag" />;
          }
          return (
            <>
              {
                record[value] ? (
                  <div className="obd-table-flex">
                    {value === 'country' && ImgDom}
                    <span>{getDynamicLocalCnEnText(value, record[value], gameCode, getLocale(), selector)}</span>
                  </div>
                ) : "-"
              }
            </>
          );
        }
      };
    });
  };
  // 指标列
  // 通常情况下metricsKey就是metricsInfo对象的key, 但是国家对比页面的metricsKey是受控的
  const getMetricColumns = (metricsKey: string[], metricsInfo: { [x: string]: any; }, explainInfo: IExplain, selectType: string, metricsData?: Record<string, any>) => {
    const isShowUpDown = selectType !== "value_only";

    // 取消勾选分日期，表头逻辑更改
    if (!hasDate && dataKey === "event_comparison") {
      const notDateColumns = metricsKey.map((key: string, index: number) => {
        const info = metricsInfo[key];
        const { metricsConfig } = info || {};
        const { type, rainbowKey, enPercentWidth, cnPercentWidth, enWidth, cnWidth, enName, cnName, explainEn, explainCn } = metricsConfig || {};
        // 使用 resolveAndSelectName 解析指标名称
        const name = resolveAndSelectName(isEn, enName, cnName, metricsData?.metricsInfo) || '';
        const isPercent = type === 'percent';
        // 兼容老的 explainInfo,后续配置更新完可删除
        const explainOld = getExplainVal(explainInfo, rainbowKey, getLocale(), explainEn, explainCn);
        let explain = resolveAndSelectName(isEn, explainEn, explainCn, metricsData?.metricsInfo, undefined, explainOld) || '';
        if (!explain || (explain.includes('{{') && explain.includes('}}'))) {
          explain = undefined;
        }
        const preWidth = name?.length * 8;
        const width = preWidth + (isEn ? enPercentWidth : cnPercentWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
        const onlyValueWidth = preWidth + (isEn ? enWidth : cnWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
        return {
          index: index + 2,
          key: key + selectType,
          copyKey: key,
          selectType,
          header: name,
          align: "right",
          explain,
          width: isShowUpDown ? width : onlyValueWidth,
          isPercent,
          render(record: any) {
            const metric = record?.metrics?.[key] || {};
            const { progressWidth, metricValue } = metric;
            return (
              <>
                <div className={"table-text-bg"} style={{ width: `${progressWidth}%` }} />
                <MetricAndUpDown
                  metric={metricValue}
                  updown={isShowUpDown ? { data: metric[`${selectType}PercentStr`], status: metric[`${selectType}Status`] } : null}
                  hasTooltip={false}
                />
              </>);
          }
        };
      });
      return notDateColumns;
    }

    return metricsKey.reduce((columns: any, v: string, t: number, _index) => {
      const info = metricsInfo[v];
      const { metricsConfig } = info || {};
      const { type, rainbowKey, enPercentWidth, cnPercentWidth, enWidth, cnWidth, enName, cnName, enContrastWidth, cnContrastWidth, explainEn, explainCn } = metricsConfig || {};
      // 使用 resolveAndSelectName 解析指标名称
      const name = resolveAndSelectName(isEn, enName, cnName, metricsData?.metricsInfo) || '';
      const isPercent = type === 'percent';
      // 兼容老的 explainInfo,后续配置更新完可删除
      const explainOld = getExplainVal(explainInfo, rainbowKey, getLocale(), explainEn, explainCn);
      let explain = resolveAndSelectName(isEn, explainEn, explainCn, metricsData?.metricsInfo, undefined, explainOld) || '';
      if (!explain || (explain.includes('{{') && explain.includes('}}'))) {
        explain = undefined;
      }

      if (dataKey === "event_comparison") {
        comparisonDate?.map((item: any, index: number) => {
          const key = index === 0 ? v : 'contrast_' + `${index > 1 ? `${index  }_${  v}` : v}`;
          const fullName = item?.name || '';
          const preWidth = fullName.length * 8;
          const width = preWidth + (isEn ? enPercentWidth : cnPercentWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
          const onlyValueWidth = preWidth + (isEn ? enWidth : cnWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
          columns.push({
            index: t + 1,
            key: key + selectType,
            copyKey: key,
            selectType,
            header: `${fullName  } ${  name}`,
            align: "right",
            explain,
            width: isShowUpDown ? width : onlyValueWidth,
            isPercent,
            render(record) {
              const metric = record?.metrics?.[key] || {};
              const { progressWidth, metricValue } = metric;
              return (
                <>
                  <div className={"table-text-bg"} style={{ width: `${progressWidth}%` }} />
                  <MetricAndUpDown
                    metric={metricValue}
                    updown={isShowUpDown ? { data: metric[`${selectType}PercentStr`], status: metric[`${selectType}Status`] } : null}
                    hasTooltip={false}
                  />
                </>);
            }
          });
        });
      } else if (dataKey === "date_comparison") {
        const width = (isEn ? enPercentWidth : cnPercentWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
        const onlyValueWidth = (isEn ? enWidth : cnWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
        const onlyConstrastValueWidth = (isEn ? enContrastWidth : cnContrastWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;

        columns.push(
          {
            index: t + 1,
            key: v + selectType,
            copyKey: v,
            selectType,
            header: name,
            align: "right",
            width: isShowUpDown ? width : onlyValueWidth,
            explain,
            isPercent,
            render(record) {
              const metric = record?.metrics?.[v] || {};
              const { progressWidth, metricValue } = metric;
              return (
                <>
                  <div className={"table-text-bg"} style={{ width: `${progressWidth}%` }} />
                  <MetricAndUpDown
                    metric={metricValue}
                    updown={isShowUpDown ? { data: metric[`${selectType}PercentStr`], status: metric[`${selectType}Status`] } : null}
                    hasTooltip={false}
                  />
                </>);
            }
          },
        );
        if (isHaveContrast) {
          columns.push(
            {
              index: t + 1,
              key: `contrast_${v}${selectType}`,
              copyKey: `contrast_${v}`,
              selectType,
              header: `${formatMessage({ id: "common_contrast" })} ${name}`,
              align: "right",
              width: isShowUpDown ? width : onlyConstrastValueWidth,
              explain,
              isPercent,
              render(record) {
                const metric = record?.metrics?.[`contrast_${v}`] || {};
                const { progressWidth, metricValue } = metric;
                return (
                  <>
                    <div className={"table-text-bg"} style={{ width: `${progressWidth}%` }} />
                    <MetricAndUpDown
                      metric={metricValue}
                      // updown={isShowUpDown ? { data: metric[`${selectType}PercentStr`], status: metric[`${selectType}Status`] } : null}
                      hasTooltip={false}
                    />
                  </>);
              }
            }
          );
        }

      }
      return columns;


    }, []);
  };

  return {
    DATE_COLUMN,
    getAccumulateColumns,
    getMetricColumns,
  };
}

export function metricRender(
  value: number,
  percent: undefined,
  metric: string,
  type: string,
  isShowUpdown: boolean
) {
  const percentWidth = metric?.isPercent && type !== 'count' ? (value > 1 ? 100 : value * 100) : 0;
  const status = getUpAndDownStatus(percent, !!type);

  const percentStr = !!type ? '' : getVal(metric, percent, true);
  const formatValue = type === 'count' ? formatNum(value, false, true) : getVal(metric, value, false);
  return (<>
    <div className={"table-text-bg"} style={{ width: `${percentWidth}%` }} />
    <MetricAndUpDown
      metric={formatValue}
      updown={isShowUpdown ? { data: percentStr, status } : null}
      hasTooltip={false}
    />
  </>);

}


/**
 * 复制当前列，语言切换，对应当前语言的取值
 * @param data 当前行数据
 */
const getCopyRenderValues = (data: any, extendConfig?: { gameCode?: any; selector?: any; }) => {
  const isEn = getLocale() === 'en-US';
  if (!extendConfig) {
    const { dimension_name } = data || {};
    if (dimension_name) {
      Object.keys(data).forEach(key => {
        const value = data[key];
        data[key] = dimension_name?.[`${key}_${isEn ? 'en' : 'cn'}`] || value;
      });
    }
  } else {
    data = generateCopyData(data, { gameCode: extendConfig?.gameCode, selectorList: extendConfig.selector });
  }
  return data;
};

/**
 * 当 metricKey ≠ reqMetricKey 时，将 API 返回数据（以 reqMetricKey 为 key）
 * 映射到 metricKey 上。
 *
 * 不可变实现：返回新对象，不修改入参 metrics，避免污染上层 tableData。
 *
 * @param metrics        API 返回的指标数据对象
 * @param metricsInfo    指标配置（含 reqMetricKey 映射）
 * @param dataKey        'event_comparison' | 'date_comparison'
 * @param comparisonDate 对比日期列表（仅 event_comparison 用其长度计算 dKey/aKey）
 */
function applyReqMetricKeyMapping(
  metrics: Record<string, any> | undefined | null,
  metricsInfo: metricsInfoType,
  dataKey: string,
  comparisonDate: any[],
): Record<string, any> | undefined | null {
  if (!metrics) return metrics;
  const result = { ...metrics };
  Object.keys(metricsInfo).forEach(mk => {
    const reqKey = metricsInfo[mk]?.metricsConfig?.reqMetricKey;
    if (!reqKey || reqKey === mk) return;
    if (dataKey === 'event_comparison') {
      comparisonDate?.forEach((_: any, idx: number) => {
        const dKey = idx === 0 ? mk : 'contrast_' + (idx > 1 ? idx + '_' + mk : mk);
        const aKey = idx === 0 ? reqKey : 'contrast_' + (idx > 1 ? idx + '_' + reqKey : reqKey);
        if (!result[dKey] && result[aKey]) result[dKey] = result[aKey];
      });
    } else if (dataKey === 'date_comparison') {
      if (!result[mk] && result[reqKey]) result[mk] = result[reqKey];
      if (!result[`contrast_${mk}`] && result[`contrast_${reqKey}`]) {
        result[`contrast_${mk}`] = result[`contrast_${reqKey}`];
      }
    }
  });
  return result;
}

// 表格返回数据格式整理
export function useTableDataFormat(tableData: any[], dataKey: string, metricsInfo: metricsInfoType, filterVal: any, isCopyData?: boolean, extendConfig?: { gameCode?: any; selector?: any; }) {
  const { comparisonDate = [] } = filterVal || {};
  return useMemo(() => {
    if (!Array.isArray(tableData)) return [];
    return tableData.map((data) => {
      if (isCopyData) {
        data = getCopyRenderValues(data, extendConfig);
      }
      let { metrics, custom_dimensions: customDimensions, ...accumulates } = data || {};
      if (customDimensions && Array.isArray(customDimensions)) {
        customDimensions.forEach((dim: { key: string; value: string; }) => {
          if (!accumulates[dim.key]) {
            accumulates[dim.key] = dim.value;
          }
        });
      }
      // metricKey ≠ reqMetricKey 时，将 API 返回数据映射到 metricKey 上（返回新对象，避免污染 tableData）
      metrics = applyReqMetricKeyMapping(metrics, metricsInfo, dataKey, comparisonDate);
      // 指标列数据
      const processMetrics = (key: string, { value, dod, sdlw, change }: any, type: string, decimal: number | undefined, percentDecimals: number | undefined) => {
        const isPercent = type === 'percent';
        const progressWidth = isPercent ? value * 100 : 0;
        const metricValue = key === "arpu" ? formatSmallValue(value, type, decimal) : commonValueFormatter(type, value, decimal);
        const dodStatus = getUpAndDownStatus(dod, false);
        const sdlwStatus = getUpAndDownStatus(sdlw, false);
        const changeStatus = getUpAndDownStatus(change, false);
        const dodPercentStr = formatPercentNum(dod, percentDecimals, false);
        const sdlwPercentStr = formatPercentNum(sdlw, percentDecimals, false);
        const changePercentStr = formatPercentNum(change, percentDecimals, false);

        return {
          progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue,
          dodStatus,
          sdlwStatus,
          changeStatus,
          dodPercentStr,
          sdlwPercentStr,
          changePercentStr,
          ...metrics[key]
        };
      };

      Object.keys(metricsInfo).forEach(v => {
        const { metricsConfig } = metricsInfo[v] || {};
        const { type, decimal, percentDecimals } = metricsConfig;

        if (dataKey === 'event_comparison') {
          comparisonDate?.map((item: any, index: number) => {
            const key = index === 0 ? v : 'contrast_' + `${index > 1 ? `${index  }_${  v}` : v}`;
            const metricValues = metrics[key] || { value: "", dod: "", sdlw: "", change: "" };
            metrics[key] = processMetrics(key, metricValues, type, decimal, percentDecimals);
          });
        } else if (dataKey === 'date_comparison') {
          {
            const key = v;
            const metricValues = metrics[key] || { value: "", dod: "", sdlw: "", change: "" };
            metrics[key] = processMetrics(key, metricValues, type, decimal, percentDecimals);
          }
          {
            const key = `contrast_${v}`;
            const metricValues = metrics[key] || { value: "", dod: "", sdlw: "", change: "" };
            metrics[key] = processMetrics(key, metricValues, type, decimal, percentDecimals);
          }
        }
      });

      return {
        metrics,
        ...accumulates
      };
    });
  }, [tableData, getLocale()]);
}
// 表格返回统计值数据格式整理 (和tableData用同一个hook时, 会因为useMemo调用次数太多而崩溃)
export function useStatisticsDataFormat(statisticsData: any[], dataKey: string, metricsInfo: metricsInfoType, filterVal: any, showStatistic: any[], isCopyData: boolean) {
  const { formatMessage } = useIntl();
  const { comparisonDate = [] } = filterVal || {};
  const statisticsObj = useMemo(() => {
    if (!Array.isArray(statisticsData)) return {};
    return statisticsData.reduce((acc, data) => {
      let { date, metrics, type: statisticsType, ...accumulates } = data || {};
      // 日期列数据 (如果type有值， 说明是聚合数据)
      const dateStr = formatMessage({ id: `common_${statisticsType}` }); // 统计值行的数据, 日期列要展示对应的聚合标题
      // metricKey ≠ reqMetricKey 时，将 API 返回数据映射到 metricKey 上（返回新对象，避免污染 statisticsData）
      metrics = applyReqMetricKeyMapping(metrics, metricsInfo, dataKey, comparisonDate);
      // 指标列数据
      Object.keys(metricsInfo).forEach(v => {
        const { metricsConfig } = metricsInfo[v] || {};
        const { type, decimal, percentDecimals } = metricsConfig;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const processMetrics = (key: string, { value, dod, sdlw, change }: any, type: string, decimal: number | undefined, percentDecimals: number | undefined) => {
          const isPercent = type === 'percent';
          const progressWidth = isPercent && statisticsType !== 'count' ? value * 100 : 0;
          const metricValue = isCopyData ? value : statisticsType === 'count' ? formatNum(value, false, true) : commonValueFormatter(type, value, decimal);
          const dodStatus = "no";
          const sdlwStatus = "no";
          const changeStatus = "no";
          const dodPercentStr = "";
          const sdlwPercentStr = "";
          const changePercentStr = "";

          return {
            progressWidth: progressWidth > 100 ? 100 : progressWidth,
            metricValue,
            dodStatus,
            sdlwStatus,
            changeStatus,
            dodPercentStr,
            sdlwPercentStr,
            changePercentStr,
            ...metrics[key]
          };
        };
        if (dataKey === 'event_comparison') {
          comparisonDate?.map((item: any, index: number) => {
            const key = index === 0 ? v : 'contrast_' + `${index > 1 ? `${index  }_${  v}` : v}`;
            const metricValues = metrics[key] || { value: "", dod: "", sdlw: "", change: "" };
            metrics[key] = processMetrics(key, metricValues, type, decimal, percentDecimals);
          });
        } else if (dataKey === 'date_comparison') {
          {
            const key = v;
            const metricValues = metrics[key] || { value: "", dod: "", sdlw: "", change: "" };
            metrics[key] = processMetrics(key, metricValues, type, decimal, percentDecimals);
          }
          {
            const key = `contrast_${v}`;
            const metricValues = metrics[key] || { value: "", dod: "", sdlw: "", change: "" };
            metrics[key] = processMetrics(key, metricValues, type, decimal, percentDecimals);
          }
        }
      });

      acc[statisticsType] = {
        date: dateStr,
        // 周六或者周日 表格行高亮
        isSatOrSun: false,
        type: statisticsType,
        metrics,
        ...accumulates
      };
      return acc;
    }, {});
  }, [statisticsData, getLocale()]);
  return Object.keys(statisticsObj).length > 0 ? showStatistic.map(type => statisticsObj[type]) : [];
}

/**
 * 处理在 Day, Week , Month不同维度下, 显示的日期格式
 * @param dateDimension 日期维度
 * @param date 日期
 * @return 返回处理后符合产品要求的日期格式字符串
 */
export const getDateFormatString = (dateDimension: string, date: string) => {
  switch (dateDimension) {
    case "Week": {
      const weekOfDay = moment(date).format('E');
      return moment(date).subtract(Number(weekOfDay) - 1, 'days').format("YYYY-MM-DD");
    }
    case "Month": {
      return moment(date).format("YYYY-MM");
    }
    case "Day":
    default:
      return moment(date).format("YYYY-MM-DD");
  }
};

export const useMetricsColumns = (dataKey: string, statisticDate_timeType: any, configData?: any) => {
  const isEn = getLocale() === 'en-US';

  const getColumn = (configData: any, isEn: boolean, dataKey: string, statisticDate_timeType: any) => {
    const columnObj: any = {};

    // 根据statisticDate_timeType选择配置
    if (dataKey === "date_comparison") {
      // 查找对应的配置项
      const selectedConfig = configData.find((item: any) => item[statisticDate_timeType]);
      if (selectedConfig) {
        // 获取选定的配置
        const config = selectedConfig[statisticDate_timeType];
        config.forEach((item: any, index: any) => {
          const { metricKey } = item || {};
          columnObj[metricKey] = {
            index,
            metricsConfig: item,
          };
        });
        return columnObj;
      }
    }

    // 默认情况下返回传入的配置
    configData.forEach((item: any, index: any) => {
      const { metricKey } = item || {};
      columnObj[metricKey] = {
        index,
        metricsConfig: item,
      };
    });
    return columnObj;
  };


  // 返回相应的列配置
  return getColumn(configData, isEn, dataKey, statisticDate_timeType);
};


